/**
 * 설정 › 사용자 주석 (2026-10-29, docs/features/32-user-comment.md)
 *
 * 정의 하나 = 가. 영역 조회 쿼리(영역ID, 영역명) · 나. 주석 조회 쿼리(영역명, 값, 주석내용) · 다. 두 쿼리를 돌릴 연결(기본 내장 SQLite)
 *            + 이 주석을 켤 연결(1개 이상). 컬럼은 이름이 아니라 **순서**로 읽는다.
 * 다른 설정과 달리 서버에 저장하므로 「저장」 을 눌러야 반영된다. 저장하면 정의를 다시 받고 영역도 새로 받는다(리덕스).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2, FiPlay, FiSave } from 'react-icons/fi';
import { createDef, updateDef, deleteDef, testQuery, DEFAULT_TARGET } from '../services/userComments';
import { loadUserCommentDefs } from '../redux/actions/userCommentActions';
import { formatApiError } from '../utils/formatters';
import { useConfirm } from './ConfirmDialog';

const blank = () => ({ id: null, name: '', spec: { enabled: true, targetConnectionId: DEFAULT_TARGET, areaSql: '', commentSql: '', connectionIds: [] } });

/** 시험 실행 결과 — 위 몇 행 */
function TestResult({ result, need }) {
    const { t } = useTranslation();
    if (!result) return null;
    if (result.error) return <div className="stg-conflict">{result.error}</div>;
    return (
        <div className="stg-ucmt-test">
            {!result.enoughColumns && <div className="stg-conflict">{t('settings:ucmt.tooFewColumns', { need })}</div>}
            <div className="stg-table-wrap">
                <table className="stg-table">
                    <thead><tr>{result.columns.map((c, i) => <th key={i} className="stg-left">{c}</th>)}</tr></thead>
                    <tbody>
                        {result.rows.map((r, i) => (
                            // eslint-disable-next-line react/no-array-index-key -- 시험 결과의 행은 순서가 곧 정체다
                            <tr key={i}>{r.map((v, j) => <td key={j} className="stg-left">{v == null ? '' : v}</td>)}</tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <span className="stg-hint">{t('settings:ucmt.testRows', { n: result.rows.length })}{result.truncated ? ` ${t('settings:ucmt.testMore')}` : ''}</span>
        </div>
    );
}

function UserCommentSection() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const confirm = useConfirm();
    const defs = useSelector(state => state.userComments.defs);
    const connections = useSelector(state => state.schema.connections);
    const [selId, setSelId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [tests, setTests] = useState({});        // { area | comment: 결과 }

    useEffect(() => { dispatch(loadUserCommentDefs()); }, [dispatch]);

    // 고른 정의를 편집본으로
    useEffect(() => {
        if (dirty) return;
        const d = selId ? defs.find(x => x.id === selId) : null;
        if (d) setDraft({ id: d.id, name: d.name, spec: { ...blank().spec, ...d.spec } });
        else if (!selId && defs.length && !draft) setSelId(defs[0].id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selId, defs]);

    const connOptions = useMemo(() => (connections || []).filter(c => c && c.id && c.type !== 'folder'), [connections]);
    const connName = (id) => { const c = connOptions.find(x => x.id === id); return c ? c.name : id; };

    const patch = (k, v) => { setDraft(d => ({ ...d, spec: { ...d.spec, [k]: v } })); setDirty(true); };
    const pick = async (id) => {
        if (dirty) {
            const ok = await confirm({ title: t('settings:ucmt.discardTitle'), message: t('settings:ucmt.discardMessage'), confirmText: t('settings:ucmt.discard'), variant: 'danger' });
            if (!ok) return;
        }
        // 편집본은 여기서 바로 채운다 — 이미 고른 것을 다시 누르면 selId 가 그대로라 위 효과가 다시 돌지 않아
        // 편집본만 비워진 채(아래 칸이 사라진 채) 남았다(신고 2026-10-29)
        const d = defs.find(x => x.id === id);
        setDirty(false); setTests({}); setSelId(id);
        setDraft(d ? { id: d.id, name: d.name, spec: { ...blank().spec, ...d.spec } } : null);
    };
    const add = async () => {
        if (dirty) {
            const ok = await confirm({ title: t('settings:ucmt.discardTitle'), message: t('settings:ucmt.discardMessage'), confirmText: t('settings:ucmt.discard'), variant: 'danger' });
            if (!ok) return;
        }
        setSelId(null); setTests({}); setDraft(blank()); setDirty(true);
    };

    const save = useCallback(async () => {
        if (!draft) return;
        if (!draft.name.trim()) { toast.warn(t('settings:ucmt.needName')); return; }
        setBusy(true);
        try {
            const r = draft.id ? await updateDef(draft.id, draft.name, draft.spec) : await createDef(draft.name, draft.spec);
            setDirty(false);
            setSelId(r.def.id);
            setDraft({ id: r.def.id, name: r.def.name, spec: { ...blank().spec, ...r.def.spec } });
            await dispatch(loadUserCommentDefs());
            toast.success(t('settings:ucmt.saved'));
        } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(false); }
    }, [draft, dispatch, t]);

    const remove = async () => {
        if (!draft) return;
        if (draft.id) {
            const ok = await confirm({ title: t('settings:ucmt.deleteTitle'), message: t('settings:ucmt.deleteMessage', { name: draft.name }), confirmText: t('settings:ucmt.delete'), variant: 'danger' });
            if (!ok) return;
            try { await deleteDef(draft.id); await dispatch(loadUserCommentDefs()); } catch (err) { toast.error(formatApiError(err, t)); return; }
        }
        setDirty(false); setDraft(null); setSelId(null); setTests({});
    };

    const runTest = async (kind) => {
        const sql = kind === 'area' ? draft.spec.areaSql : draft.spec.commentSql;
        setTests(p => ({ ...p, [kind]: { loading: true, columns: [], rows: [] } }));
        try {
            const r = await testQuery(draft.spec.targetConnectionId || DEFAULT_TARGET, sql, kind);
            setTests(p => ({ ...p, [kind]: r }));
        } catch (err) {
            setTests(p => ({ ...p, [kind]: { error: formatApiError(err, t) } }));
        }
    };

    const toggleConn = (id) => {
        const cur = draft.spec.connectionIds || [];
        patch('connectionIds', cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
    };

    return (
        <>
            <p className="stg-note stg-note-lead">{t('settings:ucmt.intro')}</p>
            <div className="stg-snippet-list">
                {defs.map(d => (
                    <button key={d.id} type="button" className={draft && draft.id === d.id ? 'stg-snippet-item active' : 'stg-snippet-item'} onClick={() => pick(d.id)}>
                        <code>{d.name}</code>
                        <span className="stg-snippet-detail">
                            {d.spec.enabled === false ? `${t('settings:ucmt.off')} · ` : ''}
                            {t('settings:ucmt.mappedCount', { n: (d.spec.connectionIds || []).length })}
                        </span>
                    </button>
                ))}
                {draft && !draft.id && (
                    <button type="button" className="stg-snippet-item active"><code>{draft.name || t('settings:ucmt.newName')}</code></button>
                )}
                {defs.length === 0 && !draft && <div className="stg-snippet-empty">{t('settings:ucmt.empty')}</div>}
            </div>
            <div className="stg-snippet-actions">
                <button type="button" className="app-btn" onClick={add}><FiPlus size={12} />{t('settings:ucmt.add')}</button>
                <button type="button" className="app-btn" onClick={remove} disabled={!draft}><FiTrash2 size={12} />{t('settings:ucmt.delete')}</button>
                <div className="stg-spacer" />
                {dirty && <span className="stg-hint">{t('settings:ucmt.unsaved')}</span>}
                <button type="button" className="app-btn app-btn-primary" onClick={save} disabled={!draft || !dirty || busy}><FiSave size={12} />{t('settings:ucmt.save')}</button>
            </div>

            {draft && (
                <div className="stg-ucmt-form">
                    <div className="stg-row">
                        <div className="stg-row-main"><span className="stg-label">{t('settings:ucmt.name')}</span></div>
                        <input className="stg-text stg-text-wide" value={draft.name} maxLength={100}
                            onChange={(e) => { setDraft(d => ({ ...d, name: e.target.value })); setDirty(true); }} />
                    </div>
                    <label className="stg-row stg-row-clickable">
                        <div className="stg-row-main">
                            <span className="stg-label">{t('settings:ucmt.enabled')}</span>
                            <span className="stg-hint">{t('settings:ucmt.enabledHint')}</span>
                        </div>
                        <input type="checkbox" checked={draft.spec.enabled !== false} onChange={(e) => patch('enabled', e.target.checked)} />
                    </label>

                    <h3>{t('settings:ucmt.areaTitle')}</h3>
                    <p className="stg-note">{t('settings:ucmt.areaHint')}</p>
                    <textarea className="stg-snippet-template" rows={5} spellCheck={false} value={draft.spec.areaSql}
                        placeholder={t('settings:ucmt.areaPlaceholder')} onChange={(e) => patch('areaSql', e.target.value)} />
                    <div className="stg-snippet-actions">
                        <button type="button" className="app-btn" onClick={() => runTest('area')} disabled={!draft.spec.areaSql.trim() || (tests.area && tests.area.loading)}>
                            <FiPlay size={12} />{t('settings:ucmt.test')}
                        </button>
                    </div>
                    {tests.area && !tests.area.loading && <TestResult result={tests.area} need={2} />}

                    <h3>{t('settings:ucmt.commentTitle')}</h3>
                    <p className="stg-note">{t('settings:ucmt.commentHint')}</p>
                    <textarea className="stg-snippet-template" rows={6} spellCheck={false} value={draft.spec.commentSql}
                        placeholder={t('settings:ucmt.commentPlaceholder')} onChange={(e) => patch('commentSql', e.target.value)} />
                    <div className="stg-snippet-actions">
                        <button type="button" className="app-btn" onClick={() => runTest('comment')} disabled={!draft.spec.commentSql.trim() || (tests.comment && tests.comment.loading)}>
                            <FiPlay size={12} />{t('settings:ucmt.test')}
                        </button>
                    </div>
                    {tests.comment && !tests.comment.loading && <TestResult result={tests.comment} need={3} />}

                    <h3>{t('settings:ucmt.targetTitle')}</h3>
                    <div className="stg-row">
                        <div className="stg-row-main">
                            <span className="stg-label">{t('settings:ucmt.target')}</span>
                            <span className="stg-hint">{t('settings:ucmt.targetHint')}</span>
                        </div>
                        <select className="stg-select" value={draft.spec.targetConnectionId || DEFAULT_TARGET} onChange={(e) => patch('targetConnectionId', e.target.value)}>
                            {!connOptions.some(c => c.id === (draft.spec.targetConnectionId || DEFAULT_TARGET)) && (
                                <option value={draft.spec.targetConnectionId || DEFAULT_TARGET}>{connName(draft.spec.targetConnectionId || DEFAULT_TARGET)}</option>
                            )}
                            {connOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>

                    <h3>{t('settings:ucmt.mapTitle')}</h3>
                    <p className="stg-note">{t('settings:ucmt.mapHint')}</p>
                    {connOptions.map(c => (
                        <label key={c.id} className="stg-row stg-row-clickable">
                            <div className="stg-row-main">
                                <span className="stg-label">{c.name}</span>
                                {c.dbType && <span className="stg-hint">{c.dbType}</span>}
                            </div>
                            <input type="checkbox" checked={(draft.spec.connectionIds || []).includes(c.id)} onChange={() => toggleConn(c.id)} />
                        </label>
                    ))}
                    {(draft.spec.connectionIds || []).filter(id => !connOptions.some(c => c.id === id)).map(id => (
                        <label key={id} className="stg-row stg-row-clickable">
                            <div className="stg-row-main"><span className="stg-label">{id}</span><span className="stg-hint">{t('settings:ucmt.goneConn')}</span></div>
                            <input type="checkbox" checked onChange={() => toggleConn(id)} />
                        </label>
                    ))}
                </div>
            )}
        </>
    );
}

export default UserCommentSection;
